<script lang="ts" setup>
import debounce from 'lodash-es/debounce';
// @ts-expect-error vue2-teleport types
import Vue2Teleport from 'vue2-teleport';

import { activeRequests } from '@/composables/global.request-counter';
import { provideReactiveViewport } from '@/composables/useGlobalReactiveViewport';

import ThemeLoader from '@/components/common/ThemeLoader.vue';
import CircleAlertIcon from '@/components/icons/CircleAlertIcon.vue';
import CircleSuccessIcon from '@/components/icons/CircleSuccessIcon.vue';

const appBarHeight = ref(0);
const isLoading = ref(false);
const mobileMenuVisible = ref(false);

provideReactiveViewport();

function onAppBarResize(data: ResizeObserverEntry['contentRect']) {
  appBarHeight.value = data.height ?? 0;
}

provide('mobileMenuVisible', readonly(mobileMenuVisible));

provide('toggleMobileMenuVisible', (value: boolean) => {
  mobileMenuVisible.value = value;
});

const hideLoaderDebouncer = debounce((value) => {
  if (value === 0) {
    isLoading.value = false;
  }
}, 100);

// при последовательных запросах лоадер дёргается (состояние сбрасывается до 0), делаем отложенный сброс
watch(activeRequests, (value) => {
  if (value > 0) {
    if (!isLoading.value) {
      isLoading.value = true;
    }

    hideLoaderDebouncer.cancel();
  }
  else if (isLoading.value) {
    hideLoaderDebouncer(value);
  }
});
</script>

<template>
  <v-app
    :style="{
      '--app-bar-height': `${appBarHeight}px`,
    }"
  >
    <v-overlay :value="isLoading" color="bnw900" opacity="0.1" z-index="65535">
      <ThemeLoader />
    </v-overlay>

    <Vue2Teleport to="body > #app">
      <NotificationsGroup
        v-slot="{
          visible,
        }"
        data-disallow-inert
        style="left: 50%; top: 1rem; transform: translateX(-50%)"
      >
        <BaseAlert
          v-for="notificationItem in visible"
          :key="`notification-${notificationItem.id}`"
          dismissible
          elevated
          :type="notificationItem.type"
          @dismiss="() => notificationItem.remove()"
          v-on="notificationItem.listeners"
        >
          <CircleSuccessIcon v-if="notificationItem.type === 'success'" />
          <CircleAlertIcon v-else />
          <span>{{ notificationItem.text }}</span>
        </BaseAlert>
      </NotificationsGroup>
    </Vue2Teleport>

    <router-view :mobile-menu-visible="mobileMenuVisible" class="fixed-layout" name="bar" @resize="onAppBarResize" @update:mobileMenuVisible="mobileMenuVisible = $event;" />

    <Transition name="slide-x-transition" mode="out-in">
      <router-view class="flex-grow-1 fixed-layout" />
    </Transition>

    <router-view class="fixed-layout" name="footer" />
  </v-app>
</template>

<style lang="scss">
@import '@/styles/main.scss';

.v-application--wrap {
  height: 100%;
  justify-content: end;
}
</style>
